// schemas that will change to away - home in US format
const reverseSchemas = [
    1,
    3,
    5,
    22,
    23,
    25,
];

export const sortedSelectionIds = (unorderedSelections, schema = 0, isUsFormat) => {
    const selections = [...Object.values(unorderedSelections)];
    const reverse = reverseSchemas.includes(Number(schema)) && isUsFormat;

    switch(schema) {
        case '6':
            return correctScoreOrdering(selections);
        case '12':
            return overUnderOrdering(selections);
        case '22':
        case '23':
            return handicapOrdering(selections, reverse);
        case '60':
        case '63':
            return sortByPrice(selections);
        case '70':
            return swishOverUnderOrdering(selections);
        case '71':
            return swishYesNoOrdering(selections);
        default:
            return sortByTypeAndParams(selections, reverse);
    }
};

/**
 * Orders selections by type ID, extra params (if they exist) and name.
 * @param {Array} selections unordered array
 * @param {Boolean} reverse true to reverse the final order
 * @returns {Array} ordered selections IDs
 */
const sortByTypeAndParams = (selections, reverse = false) => {
    const idsSorted = selections.sort((a, b)=> {
        const aParams = a.ordering_params;
        const bParams = b.ordering_params;

        if (a.selection_type_id === b.selection_type_id) {
            if (aParams?.number && bParams?.number) {
                return aParams.number < bParams.number ? -1 : 1;
            }

            if (aParams?.upper && bParams?.upper) {
                if (aParams.upper === bParams.upper) {
                    return aParams.lower < bParams.lower ? -1 : 1;
                }

                return aParams.upper < bParams.upper ? -1 : 1;
            }

            if(!isNaN(parseInt(a.name)) && !isNaN(parseInt(b.name))) {
                return parseInt(a.name) < parseInt(b.name) ? -1 : 1;
            }

            return a.name < b.name ? -1 : 1;
        }

        return a.selection_type_id < b.selection_type_id ? -1 : 1;
    }).map(selection => selection.id);

    if (reverse) {
        return idsSorted.reverse();
    }

    return idsSorted;
};

const sortByPrice = (selections) => {
    const idsSorted = selections.sort((a, b)=> {
        return Number(a.decimal_price) < Number(b.decimal_price) ? -1 : 1;
    }).map((selection) => {
        return selection.id;
    });

    return idsSorted;
};

const correctScoreOrdering = (selections) => {
    // correct score is sorted into home/draw/away sections for display purposes
    const home = selections
        .filter(({ ordering_params }) => ordering_params?.homeScore > ordering_params?.awayScore)
        .sort((a, b) => {
            if (a.ordering_params?.homeScore === b.ordering_params?.homeScore) {
                return a.ordering_params?.awayScore < b.ordering_params?.awayScore ? -1 : 1;
            }

            return a.ordering_params?.homeScore < b.ordering_params?.homeScore ? -1 : 1;
    }).map(selection => selection.id);

    const draw = selections
        .filter(({ ordering_params }) => ordering_params?.homeScore === ordering_params?.awayScore)
        .sort((a, b) => {
            return a.ordering_params?.homeScore < b.ordering_params?.homeScore ? -1 : 1;
    }).map(selection => selection.id);

    const away = selections
        .filter(({ ordering_params }) => ordering_params?.homeScore < ordering_params?.awayScore)
        .sort((a, b) => {
            if (a.ordering_params?.awayScore === b.ordering_params?.awayScore) {
                return a.ordering_params?.homeScore < b.ordering_params?.homeScore ? -1 : 1;
            }

            return a.ordering_params?.awayScore < b.ordering_params?.awayScore ? -1 : 1;
    }).map(selection => selection.id);

    return {
        home,
        draw,
        away,
    };
};

const handicapOrdering = (selections, reverse) => {
    const idsSorted = selections.sort((a, b)=> {
        const aParams = a.ordering_params;
        const bParams = b.ordering_params;

        // order by handicap line and then by type id
        if (aParams?.handicap === bParams?.handicap) {
            return a.selection_type_id < b.selection_type_id ? -1 : 1;
        }

        return aParams?.handicap < bParams?.handicap ? -1 : 1;
    }).map((selection) => {
        return selection.id;
    });

    if (reverse) {
        return idsSorted.reverse();
    }

    return idsSorted;
};

const overUnderOrdering = (selections) => {
    const selectionsSorted = selections.sort((a, b)=> {
        const aParams = a.ordering_params;
        const bParams = b.ordering_params;

        if (!aParams || !bParams) {
            return a.specialoddsvalue < b.specialoddsvalue ? -1 : 1;
        }

        // order by line and then by type id
        if (aParams?.number === bParams?.number) {
            return a.selection_type_id < b.selection_type_id ? -1 : 1;
        }

        return aParams.number < bParams.number ? -1 : 1;
    });

    const groupedSelections = selectionsSorted.reduce((groups, current) => {
        const typeId = current.selection_type_id;
        //group selections ids by line
        const line = current?.specialoddsvalue;

        if (line) {
            if (!groups[line]) {
                groups[line] = {
                    ids:[null, null],
                };
            }

            // typeId 6 is Over | typeId 8 is Under
            if (Number(typeId) === 6) {
                groups[line].ids[0] = current.id;
            } else if (Number(typeId) === 8) {
                groups[line].ids[1] = current.id;
            }
        }

        return groups;
    }, {});

    return Object.keys(groupedSelections).map(line => ({
            label: line,
            ids: groupedSelections[line].ids,
        }));
};

/**
 * Orders and groups selections for swish Over/Under market by player.
 * @param {Array} selections unordered array
 * @return {Array} selections ids grouped by player name
 */
const swishOverUnderOrdering = (selections) => {
    const groupedSelections = selections.reduce((grouped, selection) => {
        const typeId = selection.selection_type_id;
        const {
            player,
            team,
        } = selection?.ordering_params;

        if (player) {
            if (!grouped[player]) {
                grouped[player] = {
                    ids: [null, null],
                    team,
                };
            }

            if (Number(typeId) === 6) {
                grouped[player].ids[0] = selection.id;
            } else if (Number(typeId) === 8) {
                grouped[player].ids[1] = selection.id;
            }
        }

        return grouped;
    }, {});

    //Order groups by line value
    return Object.keys(groupedSelections)
        .sort((aPlayer, bPlayer) => Number(groupedSelections[aPlayer].line) - Number(groupedSelections[bPlayer].line))
        .map(player => ({
            label: player,
            caption: groupedSelections[player].team,
            ids: groupedSelections[player].ids,
        }));
};

/**
 * Orders and groups selections for swish Yes/No market by player.
 * @param {Array} selections unordered array
 * @return {Array} selections ids grouped by player name
 */
const swishYesNoOrdering = (selections) => {
    const groupedSelections = selections.reduce((grouped, selection) => {
        const typeId = selection.selection_type_id;
        const {
            player,
            team,
        } = selection?.ordering_params;

        if (player) {
            if (!grouped[player]) {
                grouped[player] = {
                    ids: [null, null],
                    player,
                    team,
                };
            }

            if (Number(typeId) === 4) {
                grouped[player].ids[0] = selection.id;
            } else if (Number(typeId) === 5) {
                grouped[player].ids[1] = selection.id;
            }
        }

        return grouped;
    }, {});

    return Object.keys(groupedSelections).map(player => ({
            label: player,
            caption: groupedSelections[player].team,
            ids: groupedSelections[player].ids,
        }));
};

export const gameLineMappedSelectionIds = (
    unorderedSelections,
    defaultLine,
    uiLines,
    header,
    selectionSchema,
    isUsFormat
) => {
    let selections = [...Object.values(unorderedSelections)];
    const reverse = reverseSchemas.includes(Number(selectionSchema)) && isUsFormat;
    const sortedSelections = {};

    if (defaultLine && !Array.isArray(defaultLine)) {
        selections = selections.filter(({ ordering_params }) => {
            return Number(defaultLine) === ordering_params.number ||
                Number(defaultLine) === Number(ordering_params.handicap);
        });
    } else if (defaultLine.length > 0) {
        selections = selections.filter(({ id }) => defaultLine.includes(Number(id)));
    }

    selections.forEach(selection => {
        let line = '';
        if (selection.type && reverse) {
            if (selection.type === '2' || selection.type === 'Under') {
                line = uiLines[0];
            } else {
                line = uiLines[1];
            }
        } else if (selection.type && !reverse) {
            if (selection.type === '1' || selection.type === 'Over') {
                line = uiLines[0];
            } else {
                line = uiLines[1];
            }
        } else {
            line = selection.name;
        }

        sortedSelections[line] = { [header]: selection.id };
    });

    return sortedSelections;
};

/**
 * Orders selections for goalscorer UI market by price.
 * "Next" selections first, "Anytime" selections after.
 * @param {Array} selectionsGroupedByName `[...<selectionName>: [<selectionNext>, <selectionAnytime>] ]`
 * @returns {Array} ordered selections IDs as `[...{ 'name': selectionName, 'ids': [selectionIdNext, selectionIdAnytime] } ]`
 */
export const goalScorerOrdering = selectionsGroupedByName => {
    // [...{'name': <name>, 'selections': [<selectionNext>, <selectionAnytime>]}]
    return selectionsGroupedByName.sort((a, b) => {
        const [aNext, aAnytime] = a.selections;
        const [bNext, bAnytime] = b.selections;

        return Number(aNext?.decimal_price) - Number(bNext?.decimal_price)
            || Number(aAnytime?.decimal_price) - Number(bAnytime?.decimal_price);
    }).map(({ name, selections }) => ({
            name,
            'ids': selections.map(selection => selection?.id),
        })
    );
};
